import React, { useEffect, useState } from 'react';
import {
  DetailedPlayer,
  DbQuestionData,
  Mode,
  ReportInaccuracy,
} from '../../types';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Typography,
  Link,
  Box,
  styled,
  tableCellClasses,
  useTheme,
  Tooltip,
  Switch,
  FormControl,
  FormControlLabel,
  FormGroup,
  Button,
  Snackbar,
  SnackbarContent,
  CardActions,
  CardContent,
  Avatar,
  Divider,
  Badge,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// import GaugeChart from 'react-gauge-chart';
import { ScorecardLegend } from '../../../../dialog/ScorecardLegend';
import { ScorecardTableRow } from './ScorecardTableRow';
import { fetchPost } from '../../../../api/common';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import ShareIcon from '@mui/icons-material/Share';
import { ScoreChart } from '../../../../dialog/ScoreChart';
import LinkIcon from '@mui/icons-material/Link';
import { BlinkingChatButton } from '../../game/score-screen/BlinkingChatButton';
import ListIcon from '@mui/icons-material/List';
import { GiRank3 } from 'react-icons/gi';
import { track } from '../../../../api/track';
import { resizeImage } from '../../../../api/cloudinary';
import { getAvatarSrc, isTimedOut } from '../../utils';
import { PrecisionGraphicScorecardIcon } from '../score-screen/PrecisionGraphicScorecardIcons';
import './styles.css';
import { HEADER_HEIGHT } from '../../../public/ScoreScreenPublic';

export const PlayerTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    width: '100px',
    height: '50px',
    borderLeft: '1px solid #333333',
    borderRight: '1px solid #333333',
  },
}));

export const formattedScore = (score: number) =>
  score > 0 ? `+${score}` : score;

const FOOTER_HEIGHT = 150;
const SUB_HEADER_HEIGHT = 55;

type PropTypes = {
  averageDifficulty?: number;
  bannedCategories?: string[];
  centerSnackbar?: boolean;
  detailedPlayers?: Record<string, DetailedPlayer>;
  enableReportInaccuracy?: boolean;
  handleMmrResultsClick?: () => void;
  handleOpenChatClick?: () => void;
  handleReportInaccuracyRowClick?: (
    row: DbQuestionData,
    questionIndex: number
  ) => void;
  isDaily?: boolean;
  showDislike?: boolean;
  forPublic?: boolean;
  gameId?: string;
  hideTitle?: boolean;
  hideUsernameLink?: boolean;
  mode?: Mode;
  profileId?: string;
  questionSet: DbQuestionData[];
  ranked?: boolean;
  reportedInaccuracies?: ReportInaccuracy[];
  selectedPlayer?: DetailedPlayer;
  showChat?: boolean;
  unreadChat?: boolean;
};

export const ScorecardSmall = ({
  averageDifficulty,
  bannedCategories,
  centerSnackbar,
  detailedPlayers,
  enableReportInaccuracy,
  forPublic,
  gameId,
  handleMmrResultsClick,
  handleOpenChatClick,
  handleReportInaccuracyRowClick,
  isDaily,
  showDislike,
  hideTitle,
  hideUsernameLink,
  mode,
  profileId,
  questionSet,
  ranked,
  reportedInaccuracies,
  selectedPlayer,
  showChat,
  unreadChat,
}: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [playerSelf, setPlayerSelf] = useState<DetailedPlayer>();
  const [playerThem, setPlayerThem] = useState<DetailedPlayer>();
  const [showAllScores, setShowAllScores] = useState(false);
  const [runningTotals, setRunningTotals] = useState(false);
  const [openLegend, setOpenLegend] = useState(false);
  const [questionDisliked, setQuestionDisliked] = useState(false);
  const [openScoreChart, setOpenScoreChart] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [revealAnswers, setRevealAnswers] = useState(new Array(10).fill(false));

  useEffect(() => {
    if (!detailedPlayers) return;
    if (mode === 'match') {
      setPlayerThem(Object.values(detailedPlayers)[1]);
    }
    if (mode !== 'sit-n-go') {
      setPlayerSelf(Object.values(detailedPlayers)[0]);
    }
  }, [detailedPlayers]);

  const handleUsernameClick = async (e: any, profileId?: string) => {
    if (!profileId) return;
    e.stopPropagation();
    navigate(`/profile/overview/${profileId}`);
  };

  const calcDifficultyGauge = (averageDifficulty: number) =>
    Math.round(33.33 * averageDifficulty - 50) / 100;

  const handleDislikeQuestion = async (questionId: string) => {
    const res = await fetchPost('/stats/dislike/', {
      questionId: questionId,
      profileId: profileId,
    });
    setQuestionDisliked(true);
  };

  const handleShareClick = async () => {
    navigator.clipboard.writeText(`https://app.par.live/game/view/${gameId}`);
    setOpenToast(true);
    await track(profileId || null, 'scorecard.share.button');
  };

  const tableCellsTotals = (
    <>
      {enableReportInaccuracy ? <TableCell width={'2%'}></TableCell> : null}
      <TableCell
        colSpan={showDislike ? 6 : 5}
        sx={{
          textAlign: 'right',
          fontWeight: 600,
        }}
      >
        <Typography variant="h6">Totals:</Typography>
      </TableCell>
      {playerSelf ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {formattedScore(playerSelf.parScore)}
        </PlayerTableCell>
      ) : null}
      {playerThem ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {playerThem.parScore !== undefined
            ? formattedScore(playerThem.parScore)
            : 'N/A'}
        </PlayerTableCell>
      ) : null}
      {mode === 'sit-n-go' && selectedPlayer ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {selectedPlayer.parScore !== undefined
            ? formattedScore(selectedPlayer.parScore)
            : 'N/A'}
        </PlayerTableCell>
      ) : null}
    </>
  );
  const handleOpenScoreChartClick = () => setOpenScoreChart(true);

  const toastMessage = (
    <Stack direction="row" spacing={1}>
      <LinkIcon />
      <Typography fontWeight={600}>Link copied to clipboard</Typography>
    </Stack>
  );

  const shareTooltip = (
    <Stack spacing={1}>
      <Typography
        variant="body1"
        sx={{
          color:
            theme.palette.mode === 'light'
              ? theme.palette.secondary.main
              : theme.palette.text.primary,
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 1px ${theme.palette.secondary.main}, 10px 0px 20px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Won on a great answer?
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color:
            theme.palette.mode === 'light'
              ? theme.palette.secondary.main
              : theme.palette.text.primary,
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 1px ${theme.palette.secondary.main}, 10px 0px 20px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Beat a TV celebrity?
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color:
            theme.palette.mode === 'light'
              ? theme.palette.secondary.main
              : theme.palette.text.primary,
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 1px ${theme.palette.secondary.main}, 10px 0px 20px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Share with friends!
      </Typography>
    </Stack>
  );

  const handleRevealAnswerClick = (index: number) => {
    const updated = Object.assign([], revealAnswers, { [index]: true });
    setRevealAnswers(updated);
  };

  const answerAttemptTooltip = (
    questionIndex: number,
    player?: DetailedPlayer
  ) => {
    return (
      <Stack spacing={2} sx={{ padding: 1 }}>
        <Typography textAlign="center">Answer Attempts</Typography>
        {!player ? (
          <Typography variant="body1">None</Typography>
        ) : (
          player.roundScores[questionIndex].answerAttempts.map((answer) => (
            <Stack direction="row" spacing={3} minWidth={'250px'}>
              <Typography
                variant="body1"
                textAlign={'right'}
                sx={{
                  width: '40%',
                  color:
                    theme.palette.mode === 'light'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  textShadow:
                    theme.palette.mode === 'dark'
                      ? `0 0 1px ${theme.palette.primary.main}, 10px 0px 20px ${theme.palette.primary.main}, 0px 0px 16px ${theme.palette.primary.main}`
                      : 'none',
                }}
              >
                {answer.gameClock.toFixed(2)} sec
              </Typography>
              <Typography
                variant="body1"
                textAlign={'left'}
                sx={{ width: '60%' }}
              >
                "{answer.input}"
              </Typography>
            </Stack>
          ))
        )}
      </Stack>
    );
  };

  const underParScoreBallStyle = {
    bgcolor: `${theme.palette.underPar.main}40`,
    color: 'text.primary',
    textAlign: 'center',
    boxShadow: `0 0 5px 1px ${theme.palette.underPar.main}, 0 0 2px 3px ${theme.palette.underPar.main} inset`,
    height: 30,
    width: 30,
  };

  const timedoutScoreBallStyle = {
    bgcolor: `${theme.palette.error.main}40`,
    color: 'error.contrastText',
    display: 'inline-flex',
    boxShadow: `0 0 5px 1px ${theme.palette.error.main}, 0 0 2px 3px ${theme.palette.error.main} inset`,
    height: 30,
    width: 30,
  };

  const getRunningTotalScore = (player: DetailedPlayer, index: number) => {
    let total = 0;
    for (let i = 0; i <= index; i++) {
      total =
        total +
        player.roundScores[i].score +
        player.roundScores[i].penalty +
        (player.roundScores[i].fuzzyPenalty || 0);
    }
    return total;
  };

  const formatPlayerScore = (
    index: number,
    par: number,
    precisionIconLocation: 'corner' | 'bottom',
    player?: DetailedPlayer
  ) => {
    if (!player || !player.roundScores[index]) return 'N/A';
    const score = runningTotals
      ? getRunningTotalScore(player, index)
      : player.roundScores[index].score;

    let scoreStyle: any = {
      bgcolor: theme.palette.mode === 'dark' ? '#434343' : 'undefined',
      color: 'error.contrastText',
      height: 30,
      width: 30,
    };
    let avatarShape: any = 'rounded';
    if (score < 0 && !runningTotals) {
      scoreStyle = underParScoreBallStyle;
      avatarShape = 'round';
    }
    if (isTimedOut(par, score) && !runningTotals) {
      scoreStyle = timedoutScoreBallStyle;
      avatarShape = 'square';
    }
    if (runningTotals) {
      scoreStyle = {
        bgcolor: theme.palette.mode === 'dark' ? 'transparent' : 'undefined',
        color: 'error.contrastText',
        display: 'inline-flex',
      };
    }
    return (
      <Stack
        spacing={precisionIconLocation === 'bottom' ? 2 : 0}
        sx={{
          position: precisionIconLocation === 'bottom' ? 'relative' : 'none',
          alignItems: 'center',
        }}
      >
        <Badge badgeContent={player.roundScores[index].penalty} color="warning">
          <Tooltip placement="top" title={answerAttemptTooltip(index, player)}>
            <Avatar sx={scoreStyle} variant={avatarShape}>
              <Typography variant="subtitle2">
                {formattedScore(score)}
              </Typography>
            </Avatar>
          </Tooltip>
        </Badge>
        {player.roundScores[index].fuzzyPenalty !== null ? (
          <PrecisionGraphicScorecardIcon
            fuzzyPenalty={player.roundScores[index].fuzzyPenalty}
            position={
              precisionIconLocation === 'bottom' ? 'relative' : 'absolute'
            }
          ></PrecisionGraphicScorecardIcon>
        ) : null}
      </Stack>
    );
  };

  return (
    <>
      <Stack
        direction={'row'}
        spacing={1}
        justifyContent={'right'}
        marginRight={1}
        height={`${SUB_HEADER_HEIGHT}px`}
      >
        {Object.values(detailedPlayers).map((p) => (
          <Stack
            sx={{
              alignItems: 'center',
            }}
          >
            <Avatar
              src={resizeImage(p.avatarPublicId, 50, 50)}
              variant="rounded"
              sx={{
                height: 30,
                width: 30,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                textAlign: 'center',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                width: '30px',
              }}
            >
              {p.username}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Box
        className="scorescreen-small-body"
        sx={{
          maxHeight: `calc(100vh - ${
            HEADER_HEIGHT + SUB_HEADER_HEIGHT + FOOTER_HEIGHT + 50
          }px)`,
          overflow: 'auto',
        }}
      >
        {questionSet.map((q, index) => (
          <>
            <CardContent
              sx={{ padding: 1, border: '1px solid', borderColor: '#515151' }}
            >
              <Stack spacing={1}>
                <Stack direction={'row'} spacing={2}>
                  <Avatar
                    sx={{
                      height: 25,
                      width: 25,
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Typography variant="subtitle2">
                    PAR {q.par} | {q.category}
                  </Typography>
                </Stack>
                <Typography variant={'caption'}>{q.question}</Typography>
                <Divider />
                <Typography
                  textAlign={'center'}
                  sx={{ color: theme.palette.dailies.main }}
                >
                  {q.answer}
                </Typography>
              </Stack>
            </CardContent>
            <Stack
              direction="row"
              spacing={1}
              paddingY={1}
              justifyContent={'right'}
              marginRight={1}
            >
              {Object.values(detailedPlayers).map((p) => (
                <Stack
                  direction={'row'}
                  position="relative"
                  justifyContent={'center'}
                >
                  {formatPlayerScore(index, q.par, 'bottom', p)}
                </Stack>
              ))}
            </Stack>
          </>
        ))}
      </Box>
      <Stack
        spacing={1}
        marginTop={2}
        height={`${FOOTER_HEIGHT}px`}
        marginRight={1}
      >
        <Typography
          textAlign={'center'}
          sx={{
            letterSpacing: 10,
            border: '1px solid',
            borderColor: theme.palette.primary.main,
          }}
        >
          TOTALS
        </Typography>
        <Stack
          direction={'row'}
          spacing={1}
          justifyContent={'right'}
          marginRight={1}
        >
          {Object.values(detailedPlayers).map((p) => (
            <Stack
              sx={{
                alignItems: 'center',
              }}
            >
              <Avatar
                src={getAvatarSrc(p?.avatarPublicId, p?.avatarUrl, 50, 50)}
                variant="rounded"
                sx={{
                  height: 30,
                  width: 30,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  textAlign: 'center',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  width: '30px',
                }}
              >
                {p.username}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Stack direction={'row'} spacing={1} justifyContent={'right'}>
          {Object.values(detailedPlayers).map((p) => (
            <Box
              sx={{
                height: 30,
                width: 30,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="subtitle2"
                textAlign={'center'}
                fontWeight={600}
              >
                {formattedScore(p.parScore)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </>
  );
};
